import axios, { AxiosInstance } from 'axios'
import { Club } from './clubs.repository'
import API_CONFIG from '../config/api.config'
import authRepository from './auth.repository'

export interface Assessment {
    id: number
    name: string
    secsAutoRefresh: number | null
}

export interface MetaTraderServerDetails {
    id: number
    name: string
    platformId: PlatformId
    host: string
    broker: Broker
    type: 'LIVE' | 'DEMO'
    version: string
    utcDiff: number
}

export interface PlatformId {
    id: number
    lastVersion: string
    downloadLink: string | null
    name: string
    logoUrl: string | null
    integrationType: string
    platformEnum: string
    public: boolean
}

export interface Broker {
    id: number
    name: string
    integrated: boolean
    logoUrl: string | null
    downloadUrl: string | null
    partner: Partner | null
    iburl: string | null
}

export interface Partner {
    id: number
    name: string
    logoUrl: string | null
}

export interface MetaTraderServerGroup {
    id: number
    groupName: string
    managerLogin: string
    metaTraderServerDetails: MetaTraderServerDetails
}

export interface Tournament {
    id: number
    assessment: Assessment
    createdAt: string
    creator: Club
    startTime: string
    endTime: string
    name: string
    imageUrl: string | null
    faqsUrl: string | null
    amount: number | null
    tournamentVisibility: 'PRIVATE' | 'PUBLIC' | 'ARCHIVED'
    initialBalance: number | null
    targetBalance: number | null
    mandatoryPartner: Partner | null
    antiSpoiler: boolean
    poolsize: number | null
    minsize: number | null
    poolsizeReset: number | null
    daysPostRegister: number | null
    maxsize: number | null
    defaultTicket: TicketInfo | null
    externalId: number | null
    typeTournament: string | null
    reset_product: ResetProduct | null
    metatraderServerGroupId: MetaTraderServerGroup | null
    inviteOnly: boolean
    tournamentRankingType: string
    secondTournamentRankingType: string | null
}

export interface TicketInfo {
    id: number
    name: string
    price: number
}

export interface ResetProduct {
    id: number
    name: string
    price: number
}

export interface TournamentParticipant {
    id: number
    username: string
    accountId: number
    balance: number
    equity: number
    registrationDate: string
    rank: number | null
    accountType: string | null
    accountPeriod: string | null
    showAccount: boolean
}

export interface TournamentAward {
    rank: number
    title: string
    description: string
    estimatedValue: string
}

export interface TournamentSettings {
    id: number
    restrictedAccountType: string
    tournament: Tournament | null
    imageUrl: string | null
    mainLanguage: string | null
    colors: {
        primary: string
        secondary: string
    }
    faqsUrl: string
    amount: number | null
    tournamentVisibility: string
    secondTournamentRankingType: string | null
    initialBalance: number
    targetBalance: number | null
    mandatoryPartner: Partner | null
    antiSpoiler: boolean
    poolsize: number | null
    minsize: number
    poolsizeReset: number | null
    daysPostRegister: number
    maxsize: number
    defaultTicket: TicketInfo | null
    externalId: string | null
    typeTournament: string
    minimumBalance: number | null
    reset_product: ResetProduct | null
    metatraderServerGroupId: MetaTraderServerGroup | null
    platformServerGroup: MetaTraderServerGroup | null
    inviteOnly: boolean
    urlWoocomerce: string
    publicPerformance: boolean
    autoEnroll: boolean
    multiAccount: boolean
    accountLeverage: number | null
    tournamentState: string | null
    tournamentRankingType: string
}

export interface TournamentOverview {
    tournamentId: number
    tournamentName: string
    mandatoryPartner: Partner | null
    tournamentOrganizer: string
    tournamentOrganizerColor: string
    tournamentOrganizerId: number
    startTime: string
    endTime: string
    participants: TournamentParticipant[]
    awards: TournamentAward[]
    inviteOnly: boolean
    tournamentSettings: TournamentSettings
    selectedFeatures: string[]
}

class TournamentsRepository {
    private api: AxiosInstance

    constructor() {
        this.api = axios.create({
            timeout: API_CONFIG.TIMEOUT,
            headers: {
                'Content-Type': 'application/json'
            }
        })

        // Request interceptor to add auth token
        this.api.interceptors.request.use(
            config => {
                const authHeaders = authRepository.getAuthHeader()
                console.log('Auth Headers:', authHeaders)
                if (authHeaders) {
                    config.headers = { ...config.headers, ...authHeaders }
                }
                return config
            },
            error => Promise.reject(error)
        )

        // Response interceptor for error handling
        this.api.interceptors.response.use(
            response => response,
            error => {
                if (error.response?.status === 401) {
                    // Token expired or invalid
                    authRepository.clearAuthData()
                    window.location.href = '/broker/login'
                }
                return Promise.reject(error)
            }
        )
    }


    /**
     * Get all tournaments for a specific club
     * @param clubId - The ID of the club
     * @returns Promise with the tournaments for the club
     */
    async getClubTournaments(clubId: number): Promise<Tournament[]> {
        console.log('Fetching tournaments for club ID:', clubId)
        try {
            const response = await this.api.get<Tournament[]>(
                API_CONFIG.COMPETITIONS.CLUB_COMPETITIONS(clubId)
            )
            console.log('Tournaments API Response:', response)
            return response.data
        } catch (error) {
            console.error('Error fetching club tournaments:', error)
            throw error
        }
    }

    /**
     * Get tournament overview including participants and awards
     * @param tournamentId - The ID of the tournament
     * @returns Promise with the tournament overview
     */
    async getTournamentOverview(tournamentId: number): Promise<TournamentOverview> {
        try {
            const response = await this.api.get<TournamentOverview>(
                API_CONFIG.COMPETITIONS.OVERVIEW(tournamentId)
            )
            console.log('Tournament Overview API Response:', response)
            return response.data
        } catch (error) {
            console.error('Error fetching tournament overview:', error)
            throw error
        }
    }

    /**
     * Filter tournaments into active and historical based on current date
     * @param tournaments - Array of tournaments to filter
     * @returns Object containing active and historical tournaments
     */
    filterTournaments(tournaments: Tournament[]): {
        active: Tournament[]
        historical: Tournament[]
    } {
        const now = new Date()
        return {
            active: tournaments.filter(t => {
                const endDate = new Date(t.endTime)
                return endDate > now
            }),
            historical: tournaments.filter(t => {
                const endDate = new Date(t.endTime)
                return endDate <= now
            })
        }
    }

    /**
     * Get all tournaments across all partner communities (new unified endpoint)
     * @returns Promise with all tournaments for the partner
     */
    async getAllPartnerTournaments(): Promise<UnifiedTournamentDTO[]> {
        console.log('Fetching all partner tournaments from unified endpoint')
        try {
            const response = await this.api.get<UnifiedTournamentDTO[]>(
                API_CONFIG.COMPETITIONS.ALL_TOURNAMENTS
            )
            console.log('All Partner Tournaments API Response:', response)
            return response.data
        } catch (error) {
            console.error('Error fetching all partner tournaments:', error)
            throw error
        }
    }

    /**
     * Get club information by clubId
     * @param clubId - The ID of the club
     * @returns Promise with the club information
     */
    async getClubInfo(clubId: number): Promise<ClubInfo> {
        try {
            const response = await this.api.get<any[]>(
                `${API_CONFIG.BASE_URL}/api/ext/tournaments/club?clubId=${clubId}`
            )
            // The API returns an array of tournaments, but we just need the creator (club) info from first tournament
            if (response.data && response.data.length > 0) {
                const creator = response.data[0].creator
                return {
                    id: creator.id,
                    name: creator.name,
                    imageUrl: creator.imageUrl,
                    primaryColor: creator.primaryColor,
                    partner: creator.partner ? {
                        fullLogoUrl: creator.partner.partnerBrand?.fullLogo || creator.partner.fullLogoUrl,
                        responsiveLogoUrl: creator.partner.partnerBrand?.responsiveLogo || creator.partner.responsiveLogoUrl
                    } : undefined
                }
            }
            throw new Error('No club data found')
        } catch (error) {
            console.error('Error fetching club info:', error)
            throw error
        }
    }
}

// Type definitions for unified tournament DTO
export interface UnifiedTournamentDTO {
    tournamentId: number
    tournamentName: string
    startTime: string
    endTime: string
    awardsCount: number
    participantsCount: number
    clubName: string
    clubId: number
    accountType: 'DEMO' | 'LIVE' | 'ALL'
}

export interface ClubInfo {
    id: number
    name: string
    imageUrl: string
    primaryColor: string
    partner?: {
        fullLogoUrl: string
        responsiveLogoUrl: string
    }
}

export default new TournamentsRepository()